// Approvals (02-DESIGN/07-guardrails/03-the-guardrails-service.md § the
// console; 01-the-console.md § the surfaces, § conventions): the person's own
// requests waiting for confirmation and, for an admin, those addressed to
// admins — one table, newest first, each row who asked, what, by which
// guardrail and when (§ conventions: density and length); a row opens the
// request's own page with what was asked and the rule's expression; *Approve* and *Deny* in its row menu, each confirmed with its
// consequence. The list refreshes on open and on demand.

import { RefreshCw } from "lucide-react";
import { useState } from "react";

import { tenantPath } from "../../address/address.ts";
import {
  Button,
  Empty,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  plural,
  since,
  useLoad,
  when,
} from "../../ui/components.tsx";
import { Table, type Column } from "../../ui/table.tsx";
import {
  Approver,
  Decision,
  addressOf,
  Status,
  type Decision as DecisionT,
  type Record_,
} from "../../wire/guard.ts";
import { GuardRefusal } from "../guardrails/Unserved.tsx";
import { useSurface } from "../surface.tsx";
import { resolveUnder } from "./resolve.ts";
import { listWaiting } from "./addressed.ts";
import {
  APPROVE_CONSEQUENCE,
  DENY_CONSEQUENCE,
  describeSubject,
  principalWords,
  statusWords,
} from "./words.ts";

export function ApprovalsPage() {
  const s = useSurface();
  const admin = s.offered("approvals:admin");
  const [list, reload] = useLoad(`approvals/${admin ? "admin" : "member"}`, () =>
    listWaiting(s.bus, s.principal, admin),
  );
  const [err, setErr] = useState<unknown>(undefined);
  const [decided, setDecided] = useState<{ record: Record_; decision: DecisionT } | undefined>(undefined);
  const refresh = (
    <Button onClick={reload}>
      <RefreshCw strokeWidth={1.6} aria-hidden="true" />
      Refresh
    </Button>
  );
  const resolve = (record: Record_, decision: DecisionT) => {
    setErr(undefined);
    setDecided(undefined);
    resolveUnder(s.bus, addressOf(record), record.id, decision).then(
      () => {
        setDecided({ record, decision });
        reload();
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  const who = (r: Record_) => {
    const w = principalWords(r.principal, s.principal);
    return w === "you" ? "You" : w;
  };
  const columns: Column<Record_>[] = [
    {
      key: "who",
      header: "Asked by",
      width: "minmax(0, 1fr)",
      value: who,
      text: (r) => `${who(r)}${r.approver === Approver.admin ? " an admin decides" : ""}`,
      cell: (r) => (
        <span className="cell">
          <span className="cell-main">{who(r)}</span>
          {r.approver === Approver.admin && <span className="cell-sub">an admin decides</span>}
        </span>
      ),
    },
    {
      key: "request",
      header: "Request",
      width: "minmax(0, 1.6fr)",
      value: (r) => describeSubject(r.subject),
      cell: (r) => <span>{describeSubject(r.subject)}</span>,
    },
    {
      key: "rule",
      header: "Guardrail",
      narrow: false,
      value: (r) => r.rule,
      cell: (r) => <span className="small">{r.rule}</span>,
    },
    {
      key: "opened",
      header: "Asked",
      width: "130px",
      narrow: false,
      value: (r) => r.opened,
      text: (r) => since(r.opened),
      cell: (r) => <span className="small muted">{since(r.opened) || when(r.opened)}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "110px",
      facet: "Status",
      value: (r) => statusWords(r.status).word,
      cell: (r) => {
        const sw = statusWords(r.status);
        return <Pill tone={sw.tone}>{sw.word}</Pill>;
      },
    },
  ];
  return (
    <>
      <PageHead
        title="Approvals"
        line={
          admin
            ? `What waits for your confirmation: your own requests, and those addressed to ${s.name}'s admins.`
            : "What waits for your confirmation before it goes through."
        }
        action={refresh}
      />
      {err !== undefined && <NotAllowed err={err} />}
      {decided?.decision === Decision.granted && (
        <Notice kind="ok" title="Approved">
          Go back to where you asked and ask again, exactly as before; it goes through once, until{" "}
          {decided.record["grant-ttl"]} from now.
        </Notice>
      )}
      {decided?.decision === Decision.denied && (
        <Notice title="Denied">Whoever was waiting is told to stop.</Notice>
      )}
      {list.state === "pending" && <Pending what="Looking for requests" />}
      {list.state === "refused" && (
        <GuardRefusal
          err={list.err}
          what="Approvals"
          absent="Nothing can wait for your confirmation in this install: no guardrail holds a request until the guardrails service runs."
        />
      )}
      {list.state === "ready" && list.value.escalations.length === 0 && (
        <Empty>Nothing waits for you. A request a guardrail holds for your confirmation appears here.</Empty>
      )}
      {list.state === "ready" && list.value.escalations.length > 0 && (
        <>
          <p className="small faint" style={{ marginBottom: 12 }}>
            {plural(list.value.escalations.length, "request")} waiting
          </p>
          <Table
            label="Approvals"
            rows={list.value.escalations}
            rowKey={(r) => r.id}
            sort={{ key: "opened", dir: "desc" }}
            to={(r) => tenantPath(s.tenant, "approvals", r.id)}
            columns={columns}
            menu={(r) =>
              r.status === Status.open
                ? [
                    {
                      label: "Approve",
                      confirm: {
                        title: `Approve ${describeSubject(r.subject)}?`,
                        consequence: APPROVE_CONSEQUENCE,
                      },
                      onSelect: () => {
                        resolve(r, Decision.granted);
                      },
                    },
                    {
                      label: "Deny",
                      confirm: {
                        title: `Deny ${describeSubject(r.subject)}?`,
                        consequence: DENY_CONSEQUENCE,
                      },
                      onSelect: () => {
                        resolve(r, Decision.denied);
                      },
                    },
                  ]
                : []
            }
          />
        </>
      )}
    </>
  );
}
